import type React from "react";
import { useEffect, useState } from "react";
import ApiService, { type PaymentHistoryItem } from "../services/api";

interface AccountPageProps {
	userEmail: string;
	onBack: () => void;
}

export const AccountPage: React.FC<AccountPageProps> = ({
	userEmail,
	onBack,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
	const [paymentsLoading, setPaymentsLoading] = useState(true);
	const [paymentsError, setPaymentsError] = useState<string | null>(null);

	useEffect(() => {
		const fetchPaymentHistory = async () => {
			try {
				const response = await ApiService.getPaymentHistory();
				setPayments(response.payments);
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : "Failed to load payment history";
				setPaymentsError(errorMessage);
			} finally {
				setPaymentsLoading(false);
			}
		};

		fetchPaymentHistory();
	}, []);

	const handleManageSubscriptions = async () => {
		setIsLoading(true);
		setError(null);

		try {
			const result = await ApiService.createPortalSession();
			window.location.href = result.url;
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : "Unknown error";
			console.error("Error creating portal session:", errorMessage);
			setError(errorMessage);
			setIsLoading(false);
		}
	};

	const formatAmount = (cents: number, currency: string) => {
		const dollars = cents / 100;
		return `${dollars.toLocaleString()} ${currency}`;
	};

	const formatDate = (dateString: string | null) => {
		if (!dateString) return "—";
		const date = new Date(dateString);
		return date.toLocaleDateString("en-HK", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const getStatusBadgeClass = (status: string) => {
		const classMap: Record<string, string> = {
			paid: "badge badge-paid",
			pending: "badge badge-pending",
			failed: "badge badge-failed",
		};
		return classMap[status] || "badge badge-pending";
	};

	const getTypeBadgeClass = (paymentType: string) => {
		return paymentType === "recurring"
			? "badge badge-recurring"
			: "badge badge-one-time";
	};

	const formatPaymentMethod = (method: string) => {
		return method === "credit-card" ? "Card" : "Bank Transfer";
	};

	return (
		<div className="donation-form-host account-page">
			<button type="button" onClick={onBack} className="back-button">
				← Back to donation form
			</button>

			<h1>Account Settings</h1>
			<p className="subtitle">
				Logged in as: <strong>{userEmail}</strong>
			</p>

			<section className="account-section">
				<h2>Subscription Management</h2>
				<p>
					Manage your recurring donations, update payment methods, and view your
					subscription history.
				</p>

				{error && <div className="error-message">{error}</div>}

				<button
					type="button"
					onClick={handleManageSubscriptions}
					disabled={isLoading}
					className="primary-button"
				>
					{isLoading ? "Loading..." : "Manage Subscriptions"}
				</button>
			</section>

			<section className="account-section">
				<h2>Payment History</h2>

				{paymentsLoading && <p>Loading payment history...</p>}

				{paymentsError && <div className="error-message">{paymentsError}</div>}

				{!paymentsLoading && !paymentsError && payments.length === 0 && (
					<p>
						No payments found. Your payment history will appear here after you
						make a donation.
					</p>
				)}

				{!paymentsLoading && !paymentsError && payments.length > 0 && (
					<div className="payment-table-container">
						<table className="payment-table">
							<thead>
								<tr>
									<th>Date</th>
									<th>Amount</th>
									<th>Method</th>
									<th>Type</th>
									<th>Status</th>
									<th>Invoice</th>
								</tr>
							</thead>
							<tbody>
								{payments.map((payment) => (
									<tr key={payment.invoice_id}>
										<td>{formatDate(payment.payment_date)}</td>
										<td className="amount">
											{formatAmount(payment.amount_cents, payment.currency)}
										</td>
										<td>{formatPaymentMethod(payment.payment_method)}</td>
										<td>
											<span className={getTypeBadgeClass(payment.payment_type)}>
												{payment.payment_type === "recurring"
													? "Monthly"
													: "One-time"}
											</span>
										</td>
										<td>
											<span className={getStatusBadgeClass(payment.status)}>
												{payment.status.charAt(0).toUpperCase() +
													payment.status.slice(1)}
											</span>
										</td>
										<td>
											{payment.hosted_invoice_url ? (
												<a
													href={payment.hosted_invoice_url}
													target="_blank"
													rel="noopener noreferrer"
													className="invoice-link"
												>
													View →
												</a>
											) : (
												<span className="text-muted">—</span>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</div>
	);
};
